import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminPanel from './AdminPanel';
import { useWebSocketNotifications } from './hooks/useWebSocketNotifications';
import FeedbackModal from './components/FeedbackModal';
import './App.css';

// Okta authentication
import {
  getUserEmail,
  getUserIdentityId,
  getUserGroups,
  signOut,
  isAuthenticated as checkOktaAuth,
  OKTA_CONFIG
} from './auth';

// S3 service (replaces Amplify Storage)
import {
  uploadUserFile,
  listUserFiles,
  getUserFileUrl,
  deleteUserFile
} from './services/s3';

// Session API (portal metrics)
import { recordLogin } from './services/sessionAPI';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [userIdentityId, setUserIdentityId] = useState(''); // User identity ID (Okta sub claim) for S3 paths
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'admin'
  const [theme, setTheme] = useState('dark'); // 'dark' or 'light'
  const [messages, setMessages] = useState([{
    type: 'system',
    text: '👋 Welcome to CSC Doc Agent!'
  }, {
    type: 'info',
    text: '💡 Chat functionality is temporarily disabled. Please use the file upload feature to generate runbook documents.'
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [extractedFiles, setExtractedFiles] = useState([]);
  const [runbookFiles, setRunbookFiles] = useState([]);
  const [htmlDiagramFiles, setHtmlDiagramFiles] = useState([]);
  const [stage2MarkdownFiles, setStage2MarkdownFiles] = useState([]);
  const [finalMarkdownFiles, setFinalMarkdownFiles] = useState([]);
  const [finalHtmlRunbookFiles, setFinalHtmlRunbookFiles] = useState([]);
  const [issuesFiles, setIssuesFiles] = useState([]);
  const [wfCoreHtmlFiles, setWfCoreHtmlFiles] = useState([]);
  const [wfCoreMarkdownFiles, setWfCoreMarkdownFiles] = useState([]);
  const [isPolling, setIsPolling] = useState(false); // Track if actively polling for new files
  const [processingStartTime, setProcessingStartTime] = useState(null); // Track when processing started
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false); // Track feedback modal state
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Collapsible section states
  const [collapsedSections, setCollapsedSections] = useState({
    upload: false,
    extracted: false,
    runbooks: false,
    stage1: false,
    stage1Fusion: false,
    stage1WfCore: false,
    stage2: false,
    stage2Fusion: false,
    stage2WfCore: false,
    finalMarkdown: false,
    finalRunbook: false
  });

  const formatNotificationContext = (notification) => {
    const parts = [];
    if (notification?.baseName) {
      parts.push(`source: ${notification.baseName}`);
    }
    if (notification?.executionId) {
      const shortId = notification.executionId.split(':').pop();
      parts.push(`exec: ${shortId}`);
    }
    return parts.length > 0 ? ` [${parts.join(' | ')}]` : '';
  };

  const storageKey = (baseKey) => (
    userIdentityId ? `${baseKey}:${userIdentityId}` : null
  );

  const readStorageArray = (baseKey) => {
    try {
      const scopedKey = storageKey(baseKey);
      if (!scopedKey) {
        return [];
      }

      const scopedValue = localStorage.getItem(scopedKey);
      if (scopedValue) {
        return JSON.parse(scopedValue);
      }

      // One-time migration from legacy unscoped keys.
      const legacyValue = localStorage.getItem(baseKey);
      if (!legacyValue) {
        return [];
      }

      const parsed = JSON.parse(legacyValue);
      localStorage.setItem(scopedKey, JSON.stringify(parsed));
      localStorage.removeItem(baseKey);
      return parsed;
    } catch (error) {
      console.error('Error reading localStorage:', error);
      return [];
    }
  };

  const writeStorageArray = (baseKey, value) => {
    try {
      const scopedKey = storageKey(baseKey);
      if (!scopedKey) {
        return;
      }
      localStorage.setItem(scopedKey, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const removeStorageKey = (baseKey) => {
    try {
      const scopedKey = storageKey(baseKey);
      if (!scopedKey) {
        return;
      }
      localStorage.removeItem(scopedKey);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // WebSocket for real-time notifications
  const { notification: wsNotification, connectionStatus } = useWebSocketNotifications();

  const checkAuthStatus = useCallback(async () => {
    try {
      const authenticated = await checkOktaAuth();

      if (authenticated) {
        setIsAuthenticated(true);

        // Get user email from Okta token
        const email = await getUserEmail();
        setUserEmail(email || 'User');

        // Get user identity ID (sub claim from token) for S3 paths
        const identityId = await getUserIdentityId();
        if (identityId) {
          setUserIdentityId(identityId);
          console.log('User Identity ID:', identityId);
        } else {
          console.error('No user identity ID found in token');
        }

        // Check if user is in Admins group
        const groups = await getUserGroups();
        const userIsAdmin = groups.includes('Admins');
        setIsAdmin(userIsAdmin);

        console.log('User authenticated');
        console.log('User email:', email);
        console.log('User groups:', groups);
        console.log('Is admin:', userIsAdmin);

        // Record login event (fire-and-forget)
        if (email) {
          recordLogin(email);
        }
      }
    } catch (error) {
      console.log('User not authenticated');
      setIsAuthenticated(false);
      setIsAdmin(false);
    } finally {
      setIsCheckingAuth(false);
    }
  }, []);

  const handleSignOut = async () => {
    try {
      // Clear localStorage before sign out
      removeStorageKey('chatbot_uploaded_files');
      removeStorageKey('chatbot_extracted_files');
      removeStorageKey('chatbot_runbook_files');
      removeStorageKey('chatbot_html_diagram_files');
      removeStorageKey('chatbot_stage2_markdown_files');
      removeStorageKey('chatbot_final_markdown_files');
      removeStorageKey('chatbot_final_html_runbook_files');
      removeStorageKey('chatbot_issues_files');
      removeStorageKey('chatbot_wf_core_html_files');
      removeStorageKey('chatbot_wf_core_markdown_files');

      // Clear state
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUserEmail('');
      setUserIdentityId('');
      setMessages([{
        type: 'system',
        text: '👋 Welcome to CSC Doc Agent!'
      }]);
      setFiles([]);
      setExtractedFiles([]);
      setRunbookFiles([]);
      setHtmlDiagramFiles([]);
      setStage2MarkdownFiles([]);
      setFinalMarkdownFiles([]);
      setFinalHtmlRunbookFiles([]);
      setIssuesFiles([]);
      setWfCoreHtmlFiles([]);
      setWfCoreMarkdownFiles([]);

      // Sign out from Okta (will redirect to Okta sign out page)
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      // Force redirect even if sign out fails
      window.location.href = '/';
    }
  };

  // Toggle between dark and light theme
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  // Get uploaded files from localStorage
  const getUploadedFiles = () => {
    return readStorageArray('chatbot_uploaded_files');
  };

  // Save uploaded file to localStorage
  const saveUploadedFile = (fileInfo) => {
    try {
      const uploaded = getUploadedFiles();
      uploaded.push(fileInfo);
      writeStorageArray('chatbot_uploaded_files', uploaded);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Remove file from localStorage
  const removeUploadedFile = (fullPath) => {
    try {
      const uploaded = getUploadedFiles();
      const filtered = uploaded.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_uploaded_files', filtered);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  // Get extracted files from localStorage
  const getExtractedFiles = () => {
    return readStorageArray('chatbot_extracted_files');
  };

  // Save extracted file to localStorage
  const saveExtractedFile = (fileInfo) => {
    try {
      const extracted = getExtractedFiles();
      // Avoid duplicates
      if (!extracted.find(f => f.fullPath === fileInfo.fullPath)) {
        extracted.push(fileInfo);
        writeStorageArray('chatbot_extracted_files', extracted);
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Remove extracted file from localStorage
  const removeExtractedFile = (fullPath) => {
    try {
      const extracted = getExtractedFiles();
      const filtered = extracted.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_extracted_files', filtered);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  // Get runbook files from localStorage
  const getRunbookFiles = () => {
    return readStorageArray('chatbot_runbook_files');
  };

  // Save runbook file to localStorage
  // Used in WebSocket notification handler (line 981)
  // eslint-disable-next-line no-unused-vars
  const saveRunbookFile = (fileInfo) => {
    try {
      // Route individual markdown files from _stage2_markdown/ to separate storage
      if (fileInfo.fullPath && fileInfo.fullPath.includes('/_stage2_markdown/')) {
        console.log('📝 Saving Stage 2 markdown file:', fileInfo.fullPath);
        saveStage2MarkdownFile(fileInfo);
        return;
      }
      const runbooks = getRunbookFiles();
      // Avoid duplicates
      if (!runbooks.find(f => f.fullPath === fileInfo.fullPath)) {
        runbooks.push(fileInfo);
        writeStorageArray('chatbot_runbook_files', runbooks);
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Remove runbook file from localStorage
  const removeRunbookFile = (fullPath) => {
    try {
      const runbooks = getRunbookFiles();
      const filtered = runbooks.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_runbook_files', filtered);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  // Clean up function no longer needed - Stage 2 markdown files are now shown in UI

  // Get SVG diagram files from localStorage
  const getHtmlDiagramFiles = () => {
    return readStorageArray('chatbot_html_diagram_files');
  };

  // Save SVG diagram file to localStorage
  // Used in WebSocket notification handler (line 967)
  // eslint-disable-next-line no-unused-vars
  const saveHtmlDiagramFile = (fileInfo) => {
    try {
      const htmlDiagrams = getHtmlDiagramFiles();
      // Avoid duplicates
      if (!htmlDiagrams.find(f => f.fullPath === fileInfo.fullPath)) {
        htmlDiagrams.push(fileInfo);
        writeStorageArray('chatbot_html_diagram_files', htmlDiagrams);
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Remove HTML diagram file from localStorage
  const removeHtmlDiagramFile = (fullPath) => {
    try {
      const htmlDiagrams = getHtmlDiagramFiles();
      const filtered = htmlDiagrams.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_html_diagram_files', filtered);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  // Stage 2 Markdown files (individual markdown files from _stage2_markdown/) – get/save/remove
  const getStage2MarkdownFiles = () => {
    return readStorageArray('chatbot_stage2_markdown_files');
  };

  const saveStage2MarkdownFile = (fileInfo) => {
    try {
      const files = getStage2MarkdownFiles();
      if (!files.find(f => f.fullPath === fileInfo.fullPath)) {
        files.push(fileInfo);
        writeStorageArray('chatbot_stage2_markdown_files', files);
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const removeStage2MarkdownFile = (fullPath) => {
    try {
      const files = getStage2MarkdownFiles();
      const filtered = files.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_stage2_markdown_files', filtered);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  // Final Markdown file (Stage 3 combined markdown) – get/save/remove
  const getFinalMarkdownFiles = () => {
    return readStorageArray('chatbot_final_markdown_files');
  };

  const saveFinalMarkdownFile = (fileInfo) => {
    try {
      const files = getFinalMarkdownFiles();
      if (!files.find(f => f.fullPath === fileInfo.fullPath)) {
        files.push(fileInfo);
        writeStorageArray('chatbot_final_markdown_files', files);
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const removeFinalMarkdownFile = (fullPath) => {
    try {
      const files = getFinalMarkdownFiles();
      const filtered = files.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_final_markdown_files', filtered);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  // Final HTML Runbook (Stage 3 consolidated runbook) – get/save/remove
  const getFinalHtmlRunbookFiles = () => {
    return readStorageArray('chatbot_final_html_runbook_files');
  };

  const saveFinalHtmlRunbookFile = (fileInfo) => {
    try {
      const list = getFinalHtmlRunbookFiles();
      if (!list.find(f => f.fullPath === fileInfo.fullPath)) {
        list.push(fileInfo);
        writeStorageArray('chatbot_final_html_runbook_files', list);
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const removeFinalHtmlRunbookFile = (fullPath) => {
    try {
      const list = getFinalHtmlRunbookFiles();
      const filtered = list.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_final_html_runbook_files', filtered);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  // Issues Doc Files – get/save/remove
  const getIssuesFiles = () => {
    return readStorageArray('chatbot_issues_files');
  };

  const saveIssuesFile = (fileInfo) => {
    try {
      const list = getIssuesFiles();
      if (!list.find(f => f.fullPath === fileInfo.fullPath)) {
        list.push(fileInfo);
        writeStorageArray('chatbot_issues_files', list);
      }
    } catch (error) {
      console.error('Error saving issues file:', error);
    }
  };

  const removeIssuesFile = (fullPath) => {
    try {
      const list = getIssuesFiles();
      const filtered = list.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_issues_files', filtered);
      setIssuesFiles(filtered);
    } catch (error) {
      console.error('Error removing issues file:', error);
    }
  };

  const listIssuesFiles = async () => {
    try {
      const stored = getIssuesFiles();
      setIssuesFiles(stored);
    } catch (error) {
      console.error('❌ Error listing issues files:', error);
    }
  };

  // WF Core HTML Files – get/save/remove
  const getWfCoreHtmlFiles = () => {
    return readStorageArray('chatbot_wf_core_html_files');
  };

  const saveWfCoreHtmlFile = (fileInfo) => {
    try {
      const list = getWfCoreHtmlFiles();
      if (!list.find(f => f.fullPath === fileInfo.fullPath)) {
        list.push(fileInfo);
        writeStorageArray('chatbot_wf_core_html_files', list);
        setWfCoreHtmlFiles([...list]); // Update state to trigger re-render
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const removeWfCoreHtmlFile = (fullPath) => {
    try {
      const list = getWfCoreHtmlFiles();
      const filtered = list.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_wf_core_html_files', filtered);
      setWfCoreHtmlFiles([...filtered]); // Update state to trigger re-render
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  // WF Core Markdown Files – get/save/remove
  const getWfCoreMarkdownFiles = () => {
    return readStorageArray('chatbot_wf_core_markdown_files');
  };

  const saveWfCoreMarkdownFile = (fileInfo) => {
    try {
      const list = getWfCoreMarkdownFiles();
      if (!list.find(f => f.fullPath === fileInfo.fullPath)) {
        list.push(fileInfo);
        writeStorageArray('chatbot_wf_core_markdown_files', list);
        setWfCoreMarkdownFiles([...list]); // Update state to trigger re-render
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const removeWfCoreMarkdownFile = (fullPath) => {
    try {
      const list = getWfCoreMarkdownFiles();
      const filtered = list.filter(f => f.fullPath !== fullPath);
      writeStorageArray('chatbot_wf_core_markdown_files', filtered);
      setWfCoreMarkdownFiles([...filtered]); // Update state to trigger re-render
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  };

  // Clear all localStorage history
  const clearAllHistory = () => {
    if (window.confirm('Are you sure you want to clear all upload, extraction, and chat history? This will not delete files from S3.')) {
      try {
        // Clear localStorage
        removeStorageKey('chatbot_uploaded_files');
        removeStorageKey('chatbot_extracted_files');
        removeStorageKey('chatbot_runbook_files');
        removeStorageKey('chatbot_html_diagram_files');
        removeStorageKey('chatbot_stage2_markdown_files');
        removeStorageKey('chatbot_final_markdown_files');
        removeStorageKey('chatbot_final_html_runbook_files');
        removeStorageKey('chatbot_issues_files');
        removeStorageKey('chatbot_wf_core_html_files');
        removeStorageKey('chatbot_wf_core_markdown_files');

        // Reset all state
        setFiles([]);
        setRunbookFiles([]);
        setHtmlDiagramFiles([]);
        setStage2MarkdownFiles([]);
        setFinalMarkdownFiles([]);
        setFinalHtmlRunbookFiles([]);
        setIssuesFiles([]);
        setWfCoreHtmlFiles([]);
        setWfCoreMarkdownFiles([]);
        setExtractedFiles([]);

        // Reset messages to just the welcome message
        setMessages([{
          type: 'system',
          text: '👋 Welcome to CSC Doc Generator!'
        }, {
          type: 'system',
          text: '🧹 History cleared successfully. All previous uploads and extractions removed from view.'
        }]);
        
      } catch (error) {
        console.error('Error clearing localStorage:', error);
        setMessages(prev => [...prev, {
          type: 'error',
          text: `❌ Failed to clear history: ${error.message}`
        }]);
      }
    }
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Upload file to S3
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate: must be a ZIP or XLSX file
    const name = (file.name || '').toLowerCase();
    const isZip = name.endsWith('.zip');
    const isZipType = file.type === 'application/zip' || file.type === 'application/x-zip-compressed';
    const isXlsx = name.endsWith('.xlsx') || name.endsWith('.xls');
    const isXlsxType = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                       file.type === 'application/vnd.ms-excel';

    if (!(isZip || isZipType) && !(isXlsx || isXlsxType)) {
      setMessages(prev => [...prev, {
        type: 'error',
        text: '❌ Invalid file type. Please upload a ZIP file (Fusion blueprints) or XLSX file (WF Core Kickstart export).'
      }]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // User-specific path (S3 service will automatically scope to user identity ID)
    // XLSX files go to wf_core_xlsx/, ZIP files go to fusion_json_zip_sfn/
    const subPath = (isXlsx || isXlsxType)
      ? `wf_core_xlsx/${file.name}`
      : `fusion_json_zip_sfn/${file.name}`;

    // Track upload time BEFORE upload starts (subtract 1 minute to account for clock skew)
    const uploadStartTime = new Date(Date.now() - 60 * 1000);

    try {
      setLoading(true);
      setMessages(prev => [...prev, {
        type: 'system',
        text: `⏳ Uploading ${file.name} to CSC Doc Agent...`
      }]);

      // Set content type with fallback for XLSX files
      const contentType = file.type ||
        (name.endsWith('.xlsx') ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
         name.endsWith('.xls') ? 'application/vnd.ms-excel' :
         'application/zip');

      // Upload using S3 service (returns full S3 key)
      const uploadedKey = await uploadUserFile(file, subPath, contentType);

      // Save file info to localStorage
      const folder = uploadedKey.substring(0, uploadedKey.lastIndexOf('/'));
      const fileInfo = {
        name: file.name,
        fullPath: uploadedKey,
        folder: folder,
        uploadedAt: new Date().toISOString(),
        size: file.size
      };
      saveUploadedFile(fileInfo);
      
      setMessages(prev => [...prev, {
        type: 'system',
        text: `✅ File uploaded successfully: ${file.name}`
      }, {
        type: 'info',
        text: `⏳ Extracting files... (this may take 10-20 seconds)`
      }]);

      // Refresh file lists
      await listFiles();

      // Poll for extracted files (extraction can take 60-90 seconds)
      // Progress visible in workflow indicator and file sections

      const pollForExtraction = async () => {
        let attempts = 0;
        const maxAttempts = 20; // Poll for up to 100 seconds
        
        const checkExtraction = async () => {
          attempts++;
          console.log(`🔍 Polling for extracted files (attempt ${attempts}/${maxAttempts})...`);

          try {
            // Get existing extracted files from session to avoid duplicates
            const existingFiles = getExtractedFiles();
            const existingPaths = existingFiles.map(f => f.fullPath);

            // List files using S3 service
            const result = await listUserFiles('fusion_jsons_extracted/');

            console.log(`📊 Upload start time: ${uploadStartTime.toISOString()}`);
            console.log(`📊 Total items in fusion_jsons_extracted/ for user ${userIdentityId}: ${result.items?.length || 0}`);

            // Only include files created AFTER this upload started AND not already in session
            const allItems = (result.items || []).filter(item => !item.path.endsWith('/'));
            console.log(`📊 Non-folder items: ${allItems.length}`);
            
            allItems.forEach(item => {
              const itemModified = new Date(item.lastModified);
              const isNew = itemModified >= uploadStartTime;
              const isInSession = existingPaths.includes(item.path);
              console.log(`  - ${item.path.split('/').pop()}: modified=${itemModified.toISOString()}, isNew=${isNew}, inSession=${isInSession}`);
            });

            const files = allItems
              .filter(item => {
                if (existingPaths.includes(item.path)) return false;
                const itemModified = new Date(item.lastModified);
                return itemModified >= uploadStartTime;
              })
              .map(item => ({
                name: item.path.split('/').pop(),
                fullPath: item.path,
                folder: `users/${userIdentityId}/fusion_jsons_extracted`,
                detectedAt: new Date().toISOString(),
                sourceUpload: file.name,
                lastModified: item.lastModified
              }));

            console.log(`✅ Found ${files.length} NEW files in fusion_jsons_extracted/ (created after upload)`, files);

            if (files.length > 0) {
              console.log(`💾 Saving ${files.length} extracted files to localStorage...`);
              files.forEach(f => {
                console.log(`  - Saving: ${f.name}`);
                saveExtractedFile(f);
              });
              console.log(`🔄 Refreshing extracted files list...`);
              await listExtractedFiles();

              // Files now visible in "Extracted Files" section
              // Processing started - start polling for incremental file detection
              setMessages(prev => [...prev, {
                type: 'info',
                text: `⚙️ Processing ${files.length} scenarios...`
              }]);

              // Start polling for HTML diagrams and markdown files
              setIsPolling(true);
              setProcessingStartTime(new Date());
            } else if (attempts >= maxAttempts) {
              console.log(`⏱️ Max attempts reached - no JSON files found in fusion_jsons_extracted/`);
              console.log(`ℹ️ This might be a WF Core file - waiting for WebSocket notification...`);
              setMessages(prev => [...prev, {
                type: 'info',
                text: `⏳ File extraction in progress... Waiting for processing to complete.`
              }]);
            } else {
              // Continue polling
              setTimeout(checkExtraction, 5000); // Check every 5 seconds
            }
          } catch (err) {
            console.error('Error checking for extracted files:', err);
            
            // Check if it's an authentication error (session expired)
            const isAuthError = err.message?.includes('credentials') || 
                               err.message?.includes('token') || 
                               err.message?.includes('Unauthenticated') ||
                               err.name === 'NotAuthorizedException';
            
            if (isAuthError) {
              setMessages(prev => [...prev, {
                type: 'error',
                text: `⏱️ Session expired. Please refresh the page to continue.`
              }]);
              return;
            }

            // For other errors, continue polling
            if (attempts < maxAttempts) {
              setTimeout(checkExtraction, 5000);
            }
          }
        };

        // Start checking after 10 seconds (give Lambda time to start)
        setTimeout(checkExtraction, 10000);
      };

      pollForExtraction();

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);

      // Check if it's an authentication error (expired token)
      const isAuthError = error.message?.includes('401') ||
                         error.message?.includes('403') ||
                         error.message?.includes('credentials') ||
                         error.message?.includes('token') ||
                         error.message?.includes('Unauthenticated') ||
                         error.message?.includes('Unauthorized') ||
                         error.name === 'NotAuthorizedException';

      setMessages(prev => [...prev, {
        type: 'error',
        text: isAuthError
          ? `⏱️ Session expired. Please try:\n\nOption 1: Hard refresh (Ctrl+Shift+R or Cmd+Shift+R)\nOption 2: Log out and log back in`
          : `❌ Upload failed: ${error.message}`
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Upload Excel file for WF Runbook (Workfront design doc) to S3
  /* WF Core Runbook Generator - COMMENTED OUT
  const handleWfFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setMessages(prev => [...prev, { type: 'error', text: 'Please upload an Excel file (.xlsx or .xls).' }]);
      return;
    }

    const s3Path = `users/${userIdentityId}/wf_runbook_upload`;
    const uploadStartTime = new Date(Date.now() - 60 * 1000);

    try {
      setLoading(true);
      setMessages(prev => [...prev, {
        type: 'system',
        text: `⏳ Uploading ${file.name} for WF Runbook...`
      }]);

      await uploadData({
        path: `${s3Path}/${file.name}`,
        data: file,
        options: { contentType: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      }).result;

      saveWfUploadedFile({
        name: file.name,
        fullPath: `${s3Path}/${file.name}`,
        folder: s3Path,
        uploadedAt: new Date().toISOString(),
        size: file.size
      });
      setWfUploadedFiles(getWfUploadedFiles());

      setMessages(prev => [...prev, {
        type: 'system',
        text: `✅ Excel uploaded. Generating Workfront design document...`
      }]);

      wfPollingActiveRef.current = true;
      checkForWfRunbooks(uploadStartTime);

      if (wfFileInputRef.current) wfFileInputRef.current.value = '';
    } catch (error) {
      console.error('WF upload error:', error);
      const isAuthError = error.message?.includes('credentials') || error.message?.includes('token') || error.message?.includes('Unauthenticated') || error.name === 'NotAuthorizedException';
      setMessages(prev => [...prev, {
        type: 'error',
        text: isAuthError ? '⏱️ Session expired. Please refresh the page.' : `❌ Upload failed: ${error.message}`
      }]);
    } finally {
      setLoading(false);
    }
  }; */

  // List files from localStorage only (session-based)
  const listFiles = async () => {
    try {
      const uploadedFiles = getUploadedFiles();
      setFiles(uploadedFiles);
    } catch (error) {
      console.error('Error listing files:', error);
      setMessages(prev => [...prev, {
        type: 'error',
        text: `❌ Failed to list files: ${error.message}`
      }]);
    }
  };

  // Download file from S3
  const handleFileDownload = async (file) => {
    try {
      setLoading(true);

      // Extract subPath from full S3 key (remove users/{identity_id}/ prefix)
      const fullPath = file.fullPath;
      const subPath = fullPath.replace(/^users\/[^/]+\//, '');

      // Get presigned download URL from S3 service
      const presignedUrl = await getUserFileUrl(subPath);

      // Fetch the file as blob
      const response = await fetch(presignedUrl);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);

      setMessages(prev => [...prev, {
        type: 'system',
        text: `✅ Downloaded: ${file.name}`
      }]);
    } catch (error) {
      console.error('Download error:', error);

      // Check if it's an authentication error (expired token)
      const isAuthError = error.message?.includes('401') ||
                         error.message?.includes('403') ||
                         error.message?.includes('credentials') ||
                         error.message?.includes('token') ||
                         error.message?.includes('Unauthenticated') ||
                         error.message?.includes('Unauthorized');

      setMessages(prev => [...prev, {
        type: 'error',
        text: isAuthError
          ? `⏱️ Session expired. Please try:\n\nOption 1: Hard refresh (Ctrl+Shift+R or Cmd+Shift+R)\nOption 2: Log out and log back in`
          : `❌ Download failed: ${error.message}`
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Delete file from S3
  const handleFileDelete = async (file) => {
    if (!window.confirm(`Are you sure you want to delete ${file.name}?`)) {
      return;
    }

    try {
      setLoading(true);

      // Extract subPath from full S3 key (remove users/{identity_id}/ prefix)
      const fullPath = file.fullPath;
      const subPath = fullPath.replace(/^users\/[^/]+\//, '');

      // Delete using S3 service
      await deleteUserFile(subPath);

      // Remove from localStorage based on file location (handle both old and new user-specific paths)
      const fileName = file.fullPath.split('/').pop();
      const isFinalHtmlRunbook = file.fullPath.includes('/fusion_runbook/') && file.fullPath.endsWith('.html') && fileName.includes('-runbook-');
      const isFinalMarkdown = file.fullPath.includes('/fusion_runbook/') && file.fullPath.endsWith('.md') && fileName.includes('FusionSN-runbook-');
      const isIssuesDoc = file.fullPath.includes('/fusion_runbook/') && file.fullPath.endsWith('.html') && fileName.includes('-issues-');
      const isStage2Markdown = file.fullPath.includes('/_stage2_markdown/') && file.fullPath.endsWith('.md');

      if (isFinalHtmlRunbook) {
        removeFinalHtmlRunbookFile(file.fullPath);
      } else if (isFinalMarkdown) {
        removeFinalMarkdownFile(file.fullPath);
      } else if (isIssuesDoc) {
        removeIssuesFile(file.fullPath);
      } else if (isStage2Markdown) {
        removeStage2MarkdownFile(file.fullPath);
      } else if (file.fullPath.includes('/fusion_runbook/') && file.fullPath.endsWith('.html')) {
        removeHtmlDiagramFile(file.fullPath);
      } else if (file.fullPath.includes('/fusion_runbook/')) {
        removeRunbookFile(file.fullPath);
      } else if ((file.folder && file.folder.includes('/fusion_jsons_extracted')) || file.fullPath.includes('/fusion_jsons_extracted/')) {
        removeExtractedFile(file.fullPath);
      } else if (file.fullPath.includes('/wf_runbook/') && file.fullPath.endsWith('.html')) {
        removeWfCoreHtmlFile(file.fullPath);
      } else if (file.fullPath.includes('/wf_runbook/') && file.fullPath.endsWith('.md')) {
        removeWfCoreMarkdownFile(file.fullPath);
      } else {
        removeUploadedFile(file.fullPath);
      }

      setMessages(prev => [...prev, {
        type: 'system',
        text: `🗑️ Deleted: ${file.name}`
      }]);

      await listFiles();
      await listExtractedFiles();
      await listRunbookFiles();
      await listHtmlDiagramFiles();
      await listFinalHtmlRunbookFiles();
      await listIssuesFiles();
      await listWfCoreHtmlFiles();
      await listWfCoreMarkdownFiles();
    } catch (error) {
      console.error('Delete error:', error);

      // Check if it's an authentication error
      const isAuthError = error.message?.includes('401') ||
                         error.message?.includes('credentials') ||
                         error.message?.includes('token') ||
                         error.message?.includes('Unauthenticated') ||
                         error.message?.includes('Unauthorized');
      
      setMessages(prev => [...prev, {
        type: 'error',
        text: isAuthError 
          ? `⏱️ Session expired. Please refresh the page and try again.`
          : `❌ Delete failed: ${error.message}`
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Send message to chat (placeholder for future implementation)
  const sendToBedrock = async () => {
    try {
      setLoading(true);

      // TODO: Implement chat feature
      // Chat feature will be integrated with Step Functions workflow
      setMessages(prev => [...prev, {
        type: 'info',
        text: '💡 Chat feature is coming soon! For now, please use the file upload feature to generate runbook documents.'
      }]);

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        type: 'error',
        text: `❌ Chat error: ${error.message}`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    
    sendToBedrock(input);
    setInput('');
  };

  // List extracted files from localStorage only (session-based)
  const listExtractedFiles = async () => {
    try {
      const storedExtracted = getExtractedFiles();
      console.log('📦 Stored extracted files (session-based):', storedExtracted);
      console.log(`📦 Count: ${storedExtracted.length} files`);

      // Sort by most recent first
      storedExtracted.sort((a, b) => new Date(b.detectedAt) - new Date(a.detectedAt));

      console.log('📦 Setting extracted files state to:', storedExtracted.map(f => f.name));
      setExtractedFiles(storedExtracted);
      console.log('📦 Extracted files state updated');
    } catch (error) {
      console.error('❌ Error listing extracted files:', error);
    }
  };

  // List runbook files from localStorage only (session-based)
  const listRunbookFiles = async () => {
    try {
      const storedRunbooks = getRunbookFiles();
      console.log('📄 Stored runbook files (session-based):', storedRunbooks);

      // Sort by most recent first
      storedRunbooks.sort((a, b) => new Date(b.generatedAt || b.detectedAt) - new Date(a.generatedAt || a.detectedAt));

      console.log('📄 Setting runbook files state:', storedRunbooks);
      setRunbookFiles(storedRunbooks);
    } catch (error) {
      console.error('❌ Error listing runbook files:', error);
    }
  };

  // List SVG diagram files from localStorage only (session-based)
  const listHtmlDiagramFiles = async () => {
    try {
      const storedHtmlDiagrams = getHtmlDiagramFiles();
      console.log('🎨 Stored HTML diagram files (session-based):', storedHtmlDiagrams);

      // Sort by most recent first
      storedHtmlDiagrams.sort((a, b) => new Date(b.generatedAt || b.detectedAt) - new Date(a.generatedAt || a.detectedAt));

      console.log('🎨 Setting HTML diagram files state:', storedHtmlDiagrams);
      setHtmlDiagramFiles(storedHtmlDiagrams);
    } catch (error) {
      console.error('❌ Error listing SVG diagram files:', error);
    }
  };

  // List Stage 2 Markdown files from localStorage (individual markdown files)
  const listStage2MarkdownFiles = async () => {
    try {
      const stored = getStage2MarkdownFiles();
      console.log('📝 Stored Stage 2 markdown files:', stored);

      // Sort by most recent first
      stored.sort((a, b) => new Date(b.generatedAt || b.detectedAt) - new Date(a.generatedAt || a.detectedAt));

      console.log('📝 Setting Stage 2 markdown files state:', stored);
      setStage2MarkdownFiles(stored);
    } catch (error) {
      console.error('❌ Error listing Stage 2 markdown files:', error);
    }
  };

  // List Final Markdown files from localStorage (Stage 3 combined markdown)
  const listFinalMarkdownFiles = async () => {
    try {
      const stored = getFinalMarkdownFiles();
      console.log('📋 Stored final markdown files:', stored);

      // Sort by most recent first
      stored.sort((a, b) => new Date(b.generatedAt || b.detectedAt) - new Date(a.generatedAt || a.detectedAt));

      console.log('📋 Setting final markdown files state:', stored);
      setFinalMarkdownFiles(stored);
    } catch (error) {
      console.error('❌ Error listing final markdown files:', error);
    }
  };

  // List Final HTML Runbook files from localStorage (Stage 3 consolidated runbook)
  const listFinalHtmlRunbookFiles = async () => {
    try {
      const stored = getFinalHtmlRunbookFiles();
      stored.sort((a, b) => new Date(b.generatedAt || b.detectedAt) - new Date(a.generatedAt || a.detectedAt));
      setFinalHtmlRunbookFiles(stored);
    } catch (error) {
      console.error('❌ Error listing final HTML runbook files:', error);
    }
  };

  // List WF Core HTML files from localStorage (WF Core HTML runbooks)
  const listWfCoreHtmlFiles = async () => {
    try {
      const stored = getWfCoreHtmlFiles();
      console.log('📊 Stored WF Core HTML files:', stored);

      // Sort by most recent first
      stored.sort((a, b) => new Date(b.generatedAt || b.detectedAt) - new Date(a.generatedAt || a.detectedAt));

      console.log('📊 Setting WF Core HTML files state:', stored);
      setWfCoreHtmlFiles(stored);
    } catch (error) {
      console.error('❌ Error listing WF Core HTML files:', error);
    }
  };

  // List WF Core Markdown files from localStorage (WF Core Markdown runbooks)
  const listWfCoreMarkdownFiles = async () => {
    try {
      const stored = getWfCoreMarkdownFiles();
      console.log('📝 Stored WF Core Markdown files:', stored);

      // Sort by most recent first
      stored.sort((a, b) => new Date(b.generatedAt || b.detectedAt) - new Date(a.generatedAt || a.detectedAt));

      console.log('📝 Setting WF Core Markdown files state:', stored);
      setWfCoreMarkdownFiles(stored);
    } catch (error) {
      console.error('❌ Error listing WF Core Markdown files:', error);
    }
  };

  // WebSocket-only notification (no polling for runbooks)

  // ALL HOOKS MUST BE CALLED BEFORE CONDITIONAL RETURNS
  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load and apply theme preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Save theme preference and apply to document
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Initialize file lists on mount
  useEffect(() => {
    if (isAuthenticated && userIdentityId) {
      // Clear intermediate files from previous sessions on page load
      // This ensures fresh start and prevents showing old files from S3
      // Time-based filtering in polling functions ensures only newly generated files appear
      removeStorageKey('chatbot_extracted_files');
      removeStorageKey('chatbot_runbook_files');
      removeStorageKey('chatbot_html_diagram_files');
      removeStorageKey('chatbot_stage2_markdown_files');

      // DON'T clear final files - these are deliverables that should persist across sessions
      // They use WebSocket-only notifications (no polling) and take 10+ minutes to generate
      // localStorage.removeItem('chatbot_final_html_runbook_files');
      // localStorage.removeItem('chatbot_final_markdown_files');

      listFiles();
      listExtractedFiles();
      listRunbookFiles();
      listHtmlDiagramFiles();
      listStage2MarkdownFiles();
      listFinalMarkdownFiles();
      listFinalHtmlRunbookFiles();
      listWfCoreHtmlFiles();
      listWfCoreMarkdownFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userIdentityId]);

  // Handle WebSocket notifications for runbook completion
  useEffect(() => {
    if (wsNotification) {
      console.log('🔔 WebSocket notification received in App.jsx:', wsNotification);
      console.log('🔔 Notification type:', wsNotification.notificationType);
      console.log('🔔 Full notification object:', JSON.stringify(wsNotification, null, 2));
      console.log('🔔 Current messages array length:', messages.length);

      // Handle individual scenario HTML completion (Stage 1 per-scenario)
      if (wsNotification.notificationType === 'scenario-html') {
        console.log('🎨 Scenario HTML complete:', wsNotification.filename);
        const fileName = wsNotification.filename || 'scenario';
        const scenarioName = fileName.replace('.json', '').replace(/_/g, ' ');

        setMessages(prev => [...prev, {
          type: 'info',
          text: `🎨 HTML diagram generated for ${scenarioName}${formatNotificationContext(wsNotification)}`
        }]);

        // Save HTML diagram file
        if (wsNotification.htmlKey) {
          const htmlFileName = wsNotification.htmlKey.split('/').pop();
          const fileInfo = {
            name: htmlFileName,
            fullPath: wsNotification.htmlKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            detectedAt: new Date().toISOString()
          };
          console.log('  🌐 Saving HTML diagram:', htmlFileName);
          saveHtmlDiagramFile(fileInfo);
          listHtmlDiagramFiles();
        }
      }
      // Handle individual scenario markdown completion (Stage 2 per-scenario)
      else if (wsNotification.notificationType === 'scenario-markdown') {
        console.log('📝 Scenario markdown complete:', wsNotification.filename);
        const fileName = wsNotification.filename || 'scenario';
        const scenarioName = fileName.replace('.json', '').replace(/_/g, ' ');

        setMessages(prev => [...prev, {
          type: 'info',
          text: `📝 Markdown runbook generated for ${scenarioName}${formatNotificationContext(wsNotification)}`
        }]);

        // Save markdown file (internal, not shown in UI file list)
        if (wsNotification.outputKey) {
          const mdFileName = wsNotification.outputKey.split('/').pop();
          const fileInfo = {
            name: mdFileName,
            fullPath: wsNotification.outputKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            detectedAt: new Date().toISOString()
          };
          console.log('  📄 Saving stage 2 markdown:', mdFileName);
          saveRunbookFile(fileInfo);
          listStage2MarkdownFiles();
        }
      }
      // Handle Stage 1-2 completion (HTML diagrams + Markdown runbooks)
      else if (wsNotification.notificationType === 'stage1-2') {
        console.log('📄 Stage 1-2 complete: Processing HTML diagrams and markdown files');
        console.log('📄 htmlDiagramKeys:', wsNotification.htmlDiagramKeys);
        console.log('📄 markdownKeys:', wsNotification.markdownKeys);

        // Stop polling - final batch has been notified
        console.log('🛑 Stopping polling - Stage 1-2 complete');
        setIsPolling(false);

        const htmlDiagramKeys = wsNotification.htmlDiagramKeys || [];
        const markdownKeys = wsNotification.markdownKeys || [];

        // Save HTML diagram files (files will appear in cards automatically)
        htmlDiagramKeys.forEach(key => {
          const fileName = key.split('/').pop();
          const fileInfo = {
            name: fileName,
            fullPath: key,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            detectedAt: new Date().toISOString()
          };
          console.log('  🌐 Saving HTML diagram:', fileName);
          saveHtmlDiagramFile(fileInfo);
        });

        // Save markdown files (files will appear in cards automatically)
        markdownKeys.forEach(key => {
          const fileName = key.split('/').pop();
          const fileInfo = {
            name: fileName,
            fullPath: key,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            detectedAt: new Date().toISOString()
          };
          console.log('  📄 Saving markdown runbook:', fileName);
          saveRunbookFile(fileInfo);
        });

        // Refresh file lists to show new files in cards
        listHtmlDiagramFiles();
        listStage2MarkdownFiles();
        listRunbookFiles();
      }
      // Handle Markdown Ready notification (Combined markdown available before Stage 3)
      else if (wsNotification.notificationType === 'markdown-ready') {
        console.log('📝 Markdown ready: Combined markdown available');
        console.log('📝 mdKey:', wsNotification.mdKey);
        console.log('📝 runbookCount:', wsNotification.runbook_count || wsNotification.runbookCount);
        console.log('📝 baseName:', wsNotification.baseName);
        console.log('📝 executionId:', wsNotification.executionId);

        // Save combined markdown file
        const markdownKey = wsNotification.mdKey;
        if (markdownKey) {
          const mdFileName = markdownKey.split('/').pop();
          const mdFileInfo = {
            name: mdFileName,
            fullPath: markdownKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            detectedAt: new Date().toISOString()
          };
          console.log('📝 Saving combined markdown to localStorage:', mdFileInfo);
          saveFinalMarkdownFile(mdFileInfo);

          setMessages(prev => [...prev, {
            type: 'success',
            text: `📝 Combined Markdown Ready: ${mdFileName} (${wsNotification.runbook_count || wsNotification.runbookCount || 0} scenarios)${formatNotificationContext(wsNotification)} - HTML generation in progress...`
          }]);

          // Refresh file list
          listFinalMarkdownFiles();
        }
      }
      // Handle Batch Progress notification (Stage 3 HTML generation progress)
      else if (wsNotification.notificationType === 'batch-progress') {
        console.log('⏳ Batch progress update:', wsNotification);

        // Update message in chat to show progress
        setMessages(prev => {
          // Check if there's already a batch progress message
          const existingProgressIndex = prev.findIndex(msg => msg.id === 'batch-progress');

          const progressMessage = {
            type: 'info',
            text: wsNotification.message,
            id: 'batch-progress' // Use fixed ID to update same message
          };

          if (existingProgressIndex >= 0) {
            // Update existing progress message
            const newMessages = [...prev];
            newMessages[existingProgressIndex] = progressMessage;
            return newMessages;
          } else {
            // Add new progress message
            return [...prev, progressMessage];
          }
        });
      }
      // Handle WF Core HTML as soon as it is available
      else if (wsNotification.notificationType === 'wfcore-html-ready') {
        console.log('📊 WF Core HTML ready:', wsNotification.htmlKey);

        if (wsNotification.htmlKey) {
          const htmlFileName = wsNotification.htmlKey.split('/').pop();
          // const alreadySaved = getWfCoreHtmlFiles().some(f => f.fullPath === wsNotification.htmlKey);
          const htmlFileInfo = {
            name: htmlFileName,
            fullPath: wsNotification.htmlKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            detectedAt: new Date().toISOString()
          };

          saveWfCoreHtmlFile(htmlFileInfo);

          // Don't show WF Core HTML notification in UI - it's an intermediate file
          // if (!alreadySaved) {
          //   setMessages(prev => [...prev, {
          //     type: 'success',
          //     text: `📊 WF Core HTML Runbook Ready: ${htmlFileName}${formatNotificationContext(wsNotification)}`
          //   }]);
          // }

          listWfCoreHtmlFiles();
        }
      }
      // Handle WF Core Markdown as soon as it is available
      else if (wsNotification.notificationType === 'wfcore-markdown-ready') {
        const markdownKeys = wsNotification.markdownKeys || (wsNotification.markdownKey ? [wsNotification.markdownKey] : []);
        console.log('📝 WF Core Markdown ready:', markdownKeys);

        const newMessages = [];
        markdownKeys.forEach(markdownKey => {
          const mdFileName = markdownKey.split('/').pop();
          const alreadySaved = getWfCoreMarkdownFiles().some(f => f.fullPath === markdownKey);
          const mdFileInfo = {
            name: mdFileName,
            fullPath: markdownKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            detectedAt: new Date().toISOString()
          };

          saveWfCoreMarkdownFile(mdFileInfo);

          if (!alreadySaved) {
            newMessages.push({
              type: 'success',
              text: `📝 WF Core Markdown Runbook Ready: ${mdFileName}${formatNotificationContext(wsNotification)}`
            });
          }
        });

        if (newMessages.length > 0) {
          setMessages(prev => [...prev, ...newMessages]);
        }

        listWfCoreMarkdownFiles();
      }
      // Handle WF Core completion (WF Core HTML + Markdown runbooks)
      // IMPORTANT: Check wfcore-complete BEFORE final check, since WF Core also has htmlKey
      else if (wsNotification.notificationType === 'wfcore-complete') {
        console.log('📊 WF Core complete: Processing HTML and Markdown runbooks');
        console.log('📊 htmlKey:', wsNotification.htmlKey);
        console.log('📊 markdownKeys:', wsNotification.markdownKeys || wsNotification.markdownKey);

        // Stop polling - WF Core processing complete
        console.log('🛑 Stopping polling - WF Core complete');
        setIsPolling(false);

        const newMessages = [];

        // WebSocket Lambda extracts keys from wfcore_results and sends them directly
        // Save WF Core HTML file
        if (wsNotification.htmlKey) {
          const htmlFileName = wsNotification.htmlKey.split('/').pop();
          const htmlFileInfo = {
            name: htmlFileName,
            fullPath: wsNotification.htmlKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            detectedAt: new Date().toISOString()
          };
          console.log('📊 Saving WF Core HTML file:', htmlFileInfo);
          saveWfCoreHtmlFile(htmlFileInfo);

          newMessages.push({
            type: 'success',
            text: `📊 WF Core HTML Runbook Ready: ${htmlFileName}${formatNotificationContext(wsNotification)}`
          });
        }

        // Save WF Core Markdown file(s)
        const markdownKeys = wsNotification.markdownKeys || (wsNotification.markdownKey ? [wsNotification.markdownKey] : []);
        if (markdownKeys.length > 0) {
          markdownKeys.forEach(markdownKey => {
            const mdFileName = markdownKey.split('/').pop();
            const mdFileInfo = {
              name: mdFileName,
              fullPath: markdownKey,
              generatedAt: wsNotification.timestamp || new Date().toISOString(),
              detectedAt: new Date().toISOString()
            };
            console.log('📝 Saving WF Core Markdown file:', mdFileInfo);
            saveWfCoreMarkdownFile(mdFileInfo);

            newMessages.push({
              type: 'success',
              text: `📝 WF Core Markdown Runbook Ready: ${mdFileName}${formatNotificationContext(wsNotification)}`
            });
          });
        }

        // Add messages to UI
        if (newMessages.length > 0) {
          setMessages(prev => [...prev, ...newMessages]);
        }

        // Refresh file lists
        listWfCoreHtmlFiles();
        listWfCoreMarkdownFiles();
      }
      // Handle WF Core standalone final HTML completion
      else if (wsNotification.notificationType === 'wfcore-final') {
        console.log('📑 WF Core standalone final HTML complete');
        console.log('📑 htmlKey:', wsNotification.htmlKey);
        console.log('📑 status:', wsNotification.status);

        // Stop polling - processing is complete
        console.log('🛑 Stopping polling - WF Core standalone complete');
        setIsPolling(false);

        const newMessages = [];

        // Save final HTML runbook
        if (wsNotification.htmlKey) {
          const htmlFileName = wsNotification.htmlKey.split('/').pop();
          const htmlFileInfo = {
            name: htmlFileName,
            fullPath: wsNotification.htmlKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString()
          };

          console.log('📑 Saving WF Core final HTML runbook to localStorage:', htmlFileInfo);
          saveFinalHtmlRunbookFile(htmlFileInfo);

          newMessages.push({
            type: 'success',
            text: `📑 WF Core Final HTML Runbook Ready: ${htmlFileName}${formatNotificationContext(wsNotification)}`
          });
        }

        // Add messages to UI
        if (newMessages.length > 0) {
          setMessages(prev => [...prev, ...newMessages]);
        }

        // Refresh file lists
        listFinalHtmlRunbookFiles();
      }
      // Handle Stage 3 completion (Final HTML runbook + combined markdown)
      else if (wsNotification.notificationType === 'final' || wsNotification.htmlKey) {
        console.log('📑 Stage 3 complete: Final runbook ready');
        console.log('📑 htmlKey:', wsNotification.htmlKey);
        console.log('📑 mdKey:', wsNotification.mdKey);
        console.log('📑 status:', wsNotification.status);
        console.log('📑 error:', wsNotification.error);
        console.log('📑 baseName:', wsNotification.baseName);
        console.log('📑 executionId:', wsNotification.executionId);

        // Stop polling - processing is complete
        console.log('🛑 Stopping polling - Stage 3 complete');
        setIsPolling(false);

        // Remove any in-progress batch message once Stage 3 reaches a terminal state
        setMessages(prev => prev.filter(msg => msg.id !== 'batch-progress'));

        const newMessages = [];
        const stage3Succeeded = wsNotification.status !== 'failed' && Boolean(wsNotification.htmlKey);
        const finalMarkdownAlreadyAnnounced = wsNotification.mdKey
          ? messages.some(msg => (
              typeof msg.text === 'string' &&
              msg.text.includes('Combined Markdown Ready:') &&
              msg.text.includes(wsNotification.mdKey.split('/').pop())
            ))
          : false;

        // Show popup for final combined markdown file
        if (wsNotification.mdKey) {
          const mdFileName = wsNotification.mdKey.split('/').pop();
          const mdFileInfo = {
            name: mdFileName,
            fullPath: wsNotification.mdKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            detectedAt: new Date().toISOString()
          };
          console.log('📝 Saving final combined markdown to localStorage:', mdFileInfo);
          saveFinalMarkdownFile(mdFileInfo);

          if (!finalMarkdownAlreadyAnnounced) {
            newMessages.push({
              type: 'success',
              text: `📝 Final Combined Markdown Ready: ${mdFileName}${formatNotificationContext(wsNotification)}`
            });
          } else {
            console.log('📝 Skipping duplicate combined markdown success message for:', mdFileName);
          }
        }

        // Show popup for issues doc
        if (wsNotification.issuesKey) {
          const issuesFileName = wsNotification.issuesKey.split('/').pop();
          const issuesFileInfo = {
            name: issuesFileName,
            fullPath: wsNotification.issuesKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString()
          };

          console.log('📋 Saving issues doc to localStorage:', issuesFileInfo);
          saveIssuesFile(issuesFileInfo);

          newMessages.push({
            type: 'success',
            text: `📋 Issues & Recommendations Ready: ${issuesFileName}${formatNotificationContext(wsNotification)}`
          });
        }

        // Show popup for final HTML runbook
        if (stage3Succeeded) {
          const htmlFileName = wsNotification.htmlKey.split('/').pop();
          const htmlFileInfo = {
            name: htmlFileName,
            fullPath: wsNotification.htmlKey,
            generatedAt: wsNotification.timestamp || new Date().toISOString(),
            runbookCount: wsNotification.runbookCount || 0
          };

          console.log('📑 Saving final HTML runbook to localStorage:', htmlFileInfo);
          saveFinalHtmlRunbookFile(htmlFileInfo);

          newMessages.push({
            type: 'success',
            text: `📑 Final HTML Runbook Ready: ${htmlFileName} (${wsNotification.runbookCount || 0} scenarios)${formatNotificationContext(wsNotification)}`
          });
        } else {
          newMessages.push({
            type: 'warning',
            text: wsNotification.error
              ? `⚠️ Final HTML runbook generation did not complete${formatNotificationContext(wsNotification)}: ${wsNotification.error}`
              : `⚠️ Final HTML runbook generation finished without producing an HTML file${formatNotificationContext(wsNotification)}.`
          });
        }

        // Add all messages to UI
        console.log('📑 ADDING MESSAGES TO UI:', newMessages);
        setMessages(prev => {
          console.log('📑 Previous messages:', prev);
          const updatedMessages = [...prev, ...newMessages];
          console.log('📑 New messages array:', updatedMessages);
          return updatedMessages;
        });

        // Refresh file lists
        listRunbookFiles();
        listFinalMarkdownFiles();
        listFinalHtmlRunbookFiles();
        listIssuesFiles();
      }
      else {
        console.warn('⚠️ Unknown notification type:', wsNotification.notificationType);
        console.warn('⚠️ Full wsNotification:', wsNotification);
      }
    } else {
      console.log('🔔 wsNotification is null/undefined');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsNotification]);

  // Poll for files incrementally while processing is active
  useEffect(() => {
    if (!isPolling || !userIdentityId || !processingStartTime) {
      return;
    }

    console.log('🔄 Starting incremental file polling...');
    console.log('🔄 Processing start time:', processingStartTime.toISOString());

    const pollForFiles = async () => {
      try {
        console.log('🔍 Polling for new HTML and markdown files...');

        // Get existing files from localStorage to avoid duplicates
        const existingHtmlDiagrams = getHtmlDiagramFiles();
        const existingMarkdown = getRunbookFiles();
        const existingHtmlPaths = existingHtmlDiagrams.map(f => f.fullPath);
        const existingMdPaths = existingMarkdown.map(f => f.fullPath);

        let foundNewFiles = false;

        // Poll for HTML diagram files
        try {
          const htmlResult = await listUserFiles('fusion_runbook/');

          const htmlItems = (htmlResult.items || [])
            .filter(item => {
              // Only include .html files that are NOT runbook files (no "-runbook-" in name)
              const fileName = item.path.split('/').pop();
              return item.path.endsWith('.html') &&
                     !fileName.includes('-runbook-') &&
                     !existingHtmlPaths.includes(item.path);
            })
            .filter(item => {
              // Only include files created after processing started
              const itemModified = new Date(item.lastModified);
              return itemModified >= processingStartTime;
            });

          if (htmlItems.length > 0) {
            console.log(`✅ Found ${htmlItems.length} new HTML diagram file(s)`);
            htmlItems.forEach(item => {
              const fileName = item.path.split('/').pop();
              const fileInfo = {
                name: fileName,
                fullPath: item.path,
                generatedAt: item.lastModified.toISOString(),
                detectedAt: new Date().toISOString()
              };
              console.log('  🌐 Adding HTML diagram via polling:', fileName);
              saveHtmlDiagramFile(fileInfo);
              foundNewFiles = true;
            });
          }
        } catch (err) {
          console.error('Error polling HTML diagrams:', err);
        }

        // Poll for markdown files (in _stage2_markdown/ subdirectory)
        try {
          const mdResult = await listUserFiles('fusion_runbook/_stage2_markdown/');

          const mdItems = (mdResult.items || [])
            .filter(item => {
              return item.path.endsWith('.md') && !existingMdPaths.includes(item.path);
            })
            .filter(item => {
              // Only include files created after processing started
              const itemModified = new Date(item.lastModified);
              return itemModified >= processingStartTime;
            });

          if (mdItems.length > 0) {
            console.log(`✅ Found ${mdItems.length} new markdown file(s)`);
            mdItems.forEach(item => {
              const fileName = item.path.split('/').pop();
              const fileInfo = {
                name: fileName,
                fullPath: item.path,
                generatedAt: item.lastModified.toISOString(),
                detectedAt: new Date().toISOString()
              };
              console.log('  📄 Adding markdown via polling:', fileName);
              // Note: These will be filtered out by listRunbookFiles due to _stage2_markdown/ path
              // But we save them temporarily so we don't re-detect them
              saveRunbookFile(fileInfo);
              foundNewFiles = true;
            });
          }
        } catch (err) {
          console.error('Error polling markdown files:', err);
        }

        // Refresh file lists if new files were found
        if (foundNewFiles) {
          console.log('🔄 Refreshing file lists with newly detected files');
          listHtmlDiagramFiles();
          listStage2MarkdownFiles();
          listRunbookFiles();
        }

      } catch (err) {
        console.error('Error in polling:', err);

        // Check if it's an authentication error - stop polling
        const isAuthError = err.message?.includes('credentials') ||
                           err.message?.includes('token') ||
                           err.message?.includes('Unauthenticated') ||
                           err.name === 'NotAuthorizedException';

        if (isAuthError) {
          console.log('🛑 Authentication error - stopping polling');
          setIsPolling(false);
        }
      }
    };

    // Poll immediately, then every 3 seconds
    pollForFiles();
    const intervalId = setInterval(pollForFiles, 3000);

    // Cleanup interval on unmount or when polling stops
    return () => {
      console.log('🛑 Cleaning up polling interval');
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, userIdentityId, processingStartTime]);

  // Note: Authentication is handled by ProtectedRoute in index.js
  // App only renders when user is authenticated and has required group membership

  // Show loading spinner while fetching user info
  if (isCheckingAuth) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading user information...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="container">
        <div className="app-header">
          <div className="header-row-1">
            <h1>📄 CSC Doc Agent</h1>
            <div className="user-info">
              <span className="user-email">👤 {userEmail}</span>
              <button onClick={() => setFeedbackModalOpen(true)} className="feedback-btn-header">
                💬 Feedback
              </button>
              <button onClick={handleSignOut} className="sign-out-btn">
                Sign Out
              </button>
            </div>
          </div>
          <div className="header-row-2">
            <div className="header-left-controls">
              <a
                href={OKTA_CONFIG.portalUrl || 'https://dev.apps.aps-ai-eng.adobe.net'}
                className="portal-link"
                title="Return to Agentic Apps Portal"
              >
                ← App Portal
              </a>
              <button
                onClick={toggleTheme}
                className="theme-toggle"
                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
            </div>
            <div className="connection-status" title={
              connectionStatus === 'connected'
                ? 'Real-time notifications enabled'
                : connectionStatus === 'connecting'
                ? 'Connecting to notification service...'
                : connectionStatus === 'disabled'
                ? 'WebSocket notifications not configured - check page after processing'
                : 'Connection unavailable - refresh page to see updates'
            }>
              {connectionStatus === 'connected' ? (
                <span className="status-connected">🟢 Live</span>
              ) : connectionStatus === 'connecting' ? (
                <span className="status-connecting">🟡 Connecting...</span>
              ) : connectionStatus === 'disabled' ? (
                <span className="status-disabled">⚪ Manual Refresh</span>
              ) : (
                <span className="status-disconnected">🔴 Disconnected</span>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            💬 Chat
          </button>
          {isAdmin && (
            <button
              className={`tab-button ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => setActiveTab('admin')}
            >
              👥 Admin
            </button>
          )}
        </div>

        {/* Chat Tab Content */}
        {activeTab === 'chat' && (
          <>
            {/* Workflow Progress Indicator */}
            {(files.length > 0 || extractedFiles.length > 0 || runbookFiles.length > 0 || finalHtmlRunbookFiles.length > 0 || issuesFiles.length > 0) && (
              <div className="workflow-progress">
                <div className={`progress-stage ${files.length > 0 ? 'completed' : ''}`}>
                  <div className="progress-icon">📤</div>
                  <span className="progress-label">Upload</span>
                </div>
                <div className={`progress-stage ${extractedFiles.length > 0 ? 'completed' : files.length > 0 ? 'active' : ''}`}>
                  <div className="progress-icon">📦</div>
                  <span className="progress-label">Extract</span>
                </div>
                <div className={`progress-stage ${runbookFiles.length > 0 ? 'completed' : extractedFiles.length > 0 ? 'active' : ''}`}>
                  <div className="progress-icon">📄</div>
                  <span className="progress-label">Generate</span>
                </div>
                <div className={`progress-stage ${finalHtmlRunbookFiles.length > 0 ? 'completed' : runbookFiles.length > 0 ? 'active' : ''}`}>
                  <div className="progress-icon">📑</div>
                  <span className="progress-label">Finalize</span>
                </div>
              </div>
            )}

            {/* File Upload Section */}
            <div className="file-section">
          <div className="section-header">
            <h3>📁 File Management</h3>
            {(files.length > 0 || extractedFiles.length > 0 || runbookFiles.length > 0 || htmlDiagramFiles.length > 0 || finalHtmlRunbookFiles.length > 0 || issuesFiles.length > 0) && (
              <button 
                onClick={clearAllHistory}
                className="clear-history-btn"
                disabled={loading}
              >
                🧹 Clear History
              </button>
            )}
          </div>
          
          <div className="upload-grid">
            {/* CSC Runbook Generator - Supports both Fusion and WF Core */}
            <div className="upload-area">
              <h4>📝 CSC Runbook Generator</h4>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,.xlsx,.xls"
                onChange={handleFileUpload}
                disabled={loading}
                className="file-input"
                id="fusion-upload"
              />
              <label htmlFor="fusion-upload" className="file-label">
                {loading ? '⏳ Uploading...' : "📤 Upload ZIP or Excel"}
              </label>
              <p style={{fontSize: '0.85rem', color: '#888', marginTop: '8px', textAlign: 'center'}}>
                ZIP: Fusion blueprints (.json) or mixed mode (with attask.xlsx)<br/>
                Excel: WF Core Kickstart exports (.xlsx, .xls) for standalone mode
              </p>
            </div>
            {/* WF Core Runbook Generator - Excel - COMMENTED OUT */}
            {/* <div className="upload-area">
              <h4>📊 WF Core Runbook Generator</h4>
              <input
                ref={wfFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleWfFileUpload}
                disabled={loading}
                className="file-input"
                id="wf-runbook-upload"
              />
              <label htmlFor="wf-runbook-upload" className="file-label">
                {loading ? '⏳ Uploading...' : '📤 Upload Excel (.xlsx)'}
              </label>
            </div> */}
          </div>
          
          {files.length > 0 && (
            <div className="file-list">
              <h4>📄 Uploaded Files ({files.length}):</h4>
              {files.map((file, index) => (
                <div key={index} className="file-item">
                  <span className="file-name">
                    {file.name}
                  </span>
                  <div className="file-actions">
                    <button 
                      onClick={() => handleFileDownload(file)}
                      className="download-btn"
                      disabled={loading}
                    >
                      📥 Download
                    </button>
                    <button 
                      onClick={() => handleFileDelete(file)}
                      className="delete-btn"
                      disabled={loading}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {files.length === 0 && !loading && (
            <p className="no-files">No files uploaded yet.</p>
          )}
        </div>

        {/* Extracted Files Section */}
        {extractedFiles.length > 0 && (
          <div className="file-section extracted-section card-enhanced">
            <div className="section-header clickable" onClick={() => toggleSection('extracted')}>
              <div className="section-title-wrapper">
                <h3>📦 Extracted Files ({extractedFiles.length})</h3>
                <span className="status-badge stage-2">Complete</span>
              </div>
              <button className="collapse-toggle">
                {collapsedSections.extracted ? '▼' : '▲'}
              </button>
            </div>
            <p className="section-description">Files extracted from uploaded ZIP archives</p>
            <div className={`file-list collapsible-content ${collapsedSections.extracted ? 'collapsed' : ''}`}>
              {extractedFiles.map((file, index) => (
                <div key={index} className="file-item extracted-item card-slide-in" style={{animationDelay: `${index * 0.05}s`}}>
                  <span className="file-name">
                    {file.name}
                    <span className="file-badge extracted-badge">extracted</span>
                  </span>
                  <div className="file-actions">
                    <button
                      onClick={() => handleFileDownload(file)}
                      className="download-btn"
                      disabled={loading}
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={() => handleFileDelete(file)}
                      className="delete-btn"
                      disabled={loading}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stage 1: HTML Generation - Fusion & WF Core */}
        {(htmlDiagramFiles.length > 0 || wfCoreHtmlFiles.length > 0) && (
          <div className="file-section runbook-section card-enhanced">
            <div className="section-header clickable" onClick={() => toggleSection('stage1')}>
              <div className="section-title-wrapper">
                <h3>🌐 Stage 1: HTML Generation ({htmlDiagramFiles.length + wfCoreHtmlFiles.length})</h3>
                <span className="status-badge stage-1">Stage 1 Complete</span>
              </div>
              <button className="collapse-toggle">
                {collapsedSections.stage1 ? '▼' : '▲'}
              </button>
            </div>
            <p className="section-description">HTML flow diagrams and runbooks generated in parallel</p>

            <div className={`collapsible-content ${collapsedSections.stage1 ? 'collapsed' : ''}`}>
              {/* Fusion HTML Diagrams Subsection */}
              {htmlDiagramFiles.length > 0 && (
                <div className="subsection">
                  <div className="subsection-header clickable" onClick={() => toggleSection('stage1Fusion')}>
                    <div className="subsection-title-wrapper">
                      <h4>📊 Fusion HTML Diagrams ({htmlDiagramFiles.length})</h4>
                      <span className="file-badge" style={{backgroundColor: '#2196f3', fontSize: '0.75rem'}}>Fusion</span>
                    </div>
                    <button className="collapse-toggle-small">
                      {collapsedSections.stage1Fusion ? '▶' : '▼'}
                    </button>
                  </div>
                  <div className={`file-list collapsible-content ${collapsedSections.stage1Fusion ? 'collapsed' : ''}`}>
                    {htmlDiagramFiles.map((file, index) => (
                      <div key={index} className="file-item runbook-item card-slide-in" style={{animationDelay: `${index * 0.05}s`}}>
                        <span className="file-name">
                          {file.name}
                          <span className="file-badge" style={{backgroundColor: '#2196f3'}}>html</span>
                        </span>
                        <div className="file-actions">
                          <button
                            onClick={() => handleFileDownload(file)}
                            className="download-btn"
                            disabled={loading}
                          >
                            📥 Download
                          </button>
                          <button
                            onClick={() => handleFileDelete(file)}
                            className="delete-btn"
                            disabled={loading}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* WF Core HTML Runbook Subsection */}
              {wfCoreHtmlFiles.length > 0 && (
                <div className="subsection">
                  <div className="subsection-header clickable" onClick={() => toggleSection('stage1WfCore')}>
                    <div className="subsection-title-wrapper">
                      <h4>📊 WF Core ({wfCoreHtmlFiles.length})</h4>
                      <span className="file-badge" style={{backgroundColor: '#0d7377', fontSize: '0.75rem'}}>HTML</span>
                    </div>
                    <button className="collapse-toggle-small">
                      {collapsedSections.stage1WfCore ? '▶' : '▼'}
                    </button>
                  </div>
                  <div className={`file-list collapsible-content ${collapsedSections.stage1WfCore ? 'collapsed' : ''}`}>
                    {wfCoreHtmlFiles.map((file, index) => (
                      <div key={index} className="file-item runbook-item card-slide-in" style={{animationDelay: `${index * 0.05}s`}}>
                        <span className="file-name">
                          {file.name}
                          <span className="file-badge" style={{backgroundColor: '#0d7377'}}>HTML</span>
                        </span>
                        <div className="file-actions">
                          <button
                            onClick={() => handleFileDownload(file)}
                            className="download-btn"
                            disabled={loading}
                          >
                            📥 Download
                          </button>
                          <button
                            onClick={() => handleFileDelete(file)}
                            className="delete-btn"
                            disabled={loading}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stage 2: Markdown Generation - Fusion & WF Core */}
        {(stage2MarkdownFiles.length > 0 || wfCoreMarkdownFiles.length > 0) && (
          <div className="file-section runbook-section card-enhanced">
            <div className="section-header clickable" onClick={() => toggleSection('stage2')}>
              <div className="section-title-wrapper">
                <h3>📝 Stage 2: Markdown Generation ({stage2MarkdownFiles.length + wfCoreMarkdownFiles.length})</h3>
                <span className="status-badge stage-2">Stage 2 Complete</span>
              </div>
              <button className="collapse-toggle">
                {collapsedSections.stage2 ? '▼' : '▲'}
              </button>
            </div>
            <p className="section-description">Markdown documentation generated in parallel</p>

            <div className={`collapsible-content ${collapsedSections.stage2 ? 'collapsed' : ''}`}>
              {/* Fusion Individual Markdown Files Subsection */}
              {stage2MarkdownFiles.length > 0 && (
                <div className="subsection">
                  <div className="subsection-header clickable" onClick={() => toggleSection('stage2Fusion')}>
                    <div className="subsection-title-wrapper">
                      <h4>📄 Fusion Markdown Files ({stage2MarkdownFiles.length})</h4>
                      <span className="file-badge" style={{backgroundColor: '#4caf50', fontSize: '0.75rem'}}>Fusion</span>
                    </div>
                    <button className="collapse-toggle-small">
                      {collapsedSections.stage2Fusion ? '▶' : '▼'}
                    </button>
                  </div>
                  <div className={`file-list collapsible-content ${collapsedSections.stage2Fusion ? 'collapsed' : ''}`}>
                    {stage2MarkdownFiles.map((file, index) => (
                      <div key={index} className="file-item runbook-item card-slide-in" style={{animationDelay: `${index * 0.05}s`}}>
                        <span className="file-name">
                          {file.name}
                          <span className="file-badge" style={{backgroundColor: '#4caf50'}}>md</span>
                        </span>
                        <div className="file-actions">
                          <button
                            onClick={() => handleFileDownload(file)}
                            className="download-btn"
                            disabled={loading}
                          >
                            📥 Download
                          </button>
                          <button
                            onClick={() => handleFileDelete(file)}
                            className="delete-btn"
                            disabled={loading}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* WF Core Markdown Runbook Subsection */}
              {wfCoreMarkdownFiles.length > 0 && (
                <div className="subsection">
                  <div className="subsection-header clickable" onClick={() => toggleSection('stage2WfCore')}>
                    <div className="subsection-title-wrapper">
                      <h4>📄 WF Core ({wfCoreMarkdownFiles.length})</h4>
                      <span className="file-badge" style={{backgroundColor: '#14746F', fontSize: '0.75rem'}}>MD</span>
                    </div>
                    <button className="collapse-toggle-small">
                      {collapsedSections.stage2WfCore ? '▶' : '▼'}
                    </button>
                  </div>
                  <div className={`file-list collapsible-content ${collapsedSections.stage2WfCore ? 'collapsed' : ''}`}>
                    {wfCoreMarkdownFiles.map((file, index) => (
                      <div key={index} className="file-item runbook-item card-slide-in" style={{animationDelay: `${index * 0.05}s`}}>
                        <span className="file-name">
                          {file.name}
                          <span className="file-badge" style={{backgroundColor: '#14746F'}}>MD</span>
                        </span>
                        <div className="file-actions">
                          <button
                            onClick={() => handleFileDownload(file)}
                            className="download-btn"
                            disabled={loading}
                          >
                            📥 Download
                          </button>
                          <button
                            onClick={() => handleFileDelete(file)}
                            className="delete-btn"
                            disabled={loading}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Markdown Runbook Section */}
        {runbookFiles.length > 0 && (
          <div className="file-section runbook-section card-enhanced">
            <div className="section-header clickable" onClick={() => toggleSection('runbooks')}>
              <div className="section-title-wrapper">
                <h3>📄 Markdown Runbook ({runbookFiles.length})</h3>
                <span className="status-badge stage-2">Stage 2 Complete</span>
              </div>
              <button className="collapse-toggle">
                {collapsedSections.runbooks ? '▼' : '▲'}
              </button>
            </div>
            <p className="section-description">Consolidated Markdown runbook (Stage 2) – single file with all scenarios</p>
            <div className={`file-list collapsible-content ${collapsedSections.runbooks ? 'collapsed' : ''}`}>
              {runbookFiles.map((file, index) => (
                <div key={index} className="file-item runbook-item card-slide-in" style={{animationDelay: `${index * 0.05}s`}}>
                  <span className="file-name">
                    {file.name}
                    <span className="file-badge" style={{backgroundColor: '#4caf50'}}>md</span>
                  </span>
                  <div className="file-actions">
                    <button
                      onClick={() => handleFileDownload(file)}
                      className="download-btn"
                      disabled={loading}
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={() => handleFileDelete(file)}
                      className="delete-btn"
                      disabled={loading}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Final Markdown File Section (Stage 3 combined markdown) */}
        {finalMarkdownFiles.length > 0 && (
          <div className="file-section runbook-section card-enhanced card-highlight">
            <div className="section-header clickable" onClick={() => toggleSection('finalMarkdown')}>
              <div className="section-title-wrapper">
                <h3>📋 Final Markdown Runbook ({finalMarkdownFiles.length})</h3>
                <span className="status-badge stage-3">Stage 3 Complete</span>
              </div>
              <button className="collapse-toggle">
                {collapsedSections.finalMarkdown ? '▼' : '▲'}
              </button>
            </div>
            <p className="section-description">Combined markdown runbook (Stage 3) – single file with all scenarios</p>
            <div className={`file-list collapsible-content ${collapsedSections.finalMarkdown ? 'collapsed' : ''}`}>
              {finalMarkdownFiles.map((file, index) => (
                <div key={index} className="file-item runbook-item card-slide-in" style={{animationDelay: `${index * 0.05}s`}}>
                  <span className="file-name">
                    {file.name}
                    <span className="file-badge" style={{backgroundColor: '#4caf50'}}>MD</span>
                  </span>
                  <div className="file-actions">
                    <button
                      onClick={() => handleFileDownload(file)}
                      className="download-btn"
                      disabled={loading}
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={() => handleFileDelete(file)}
                      className="delete-btn"
                      disabled={loading}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Final HTML Runbook Section (Stage 3 consolidated runbook) */}
        {finalHtmlRunbookFiles.length > 0 && (
          <div className="file-section runbook-section card-enhanced card-highlight">
            <div className="section-header clickable" onClick={() => toggleSection('finalRunbook')}>
              <div className="section-title-wrapper">
                <h3>📑 Final HTML Runbook ({finalHtmlRunbookFiles.length})</h3>
                <span className="status-badge stage-3">Stage 3 Complete</span>
              </div>
              <button className="collapse-toggle">
                {collapsedSections.finalRunbook ? '▼' : '▲'}
              </button>
            </div>
            <p className="section-description">Consolidated HTML runbook (Stage 3) – single file with all scenarios</p>
            <div className={`file-list collapsible-content ${collapsedSections.finalRunbook ? 'collapsed' : ''}`}>
              {finalHtmlRunbookFiles.map((file, index) => (
                <div key={index} className="file-item runbook-item card-slide-in" style={{animationDelay: `${index * 0.05}s`}}>
                  <span className="file-name">
                    {file.name}
                    <span className="file-badge" style={{backgroundColor: '#9c27b0'}}>HTML</span>
                  </span>
                  <div className="file-actions">
                    <button
                      onClick={() => handleFileDownload(file)}
                      className="download-btn"
                      disabled={loading}
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={() => handleFileDelete(file)}
                      className="delete-btn"
                      disabled={loading}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Issues & Recommendations Section */}
        {issuesFiles.length > 0 && (
          <div className="file-section runbook-section card-enhanced card-highlight">
            <div className="section-header">
              <div className="section-title-wrapper">
                <h3>📋 Issues & Recommendations ({issuesFiles.length})</h3>
                <span className="status-badge stage-3">Stage 3 Complete</span>
              </div>
            </div>
            <p className="section-description">Identified issues and optimization recommendations covering all scenarios</p>
            <div className="file-list">
              {issuesFiles.map((file, index) => (
                <div key={index} className="file-item runbook-item card-slide-in" style={{animationDelay: `${index * 0.05}s`}}>
                  <span className="file-name">
                    {file.name}
                    <span className="file-badge" style={{backgroundColor: '#ff9800'}}>HTML</span>
                  </span>
                  <div className="file-actions">
                    <button
                      onClick={() => handleFileDownload(file)}
                      className="download-btn"
                      disabled={loading}
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={() => handleFileDelete(file)}
                      className="delete-btn"
                      disabled={loading}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Section */}
        <div className="chat-container">
          <div className="messages">
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.type}`}>
                <div className="message-content">
                  <strong className="message-label">
                    {msg.type === 'user' ? '👤 You' : 
                     msg.type === 'agent' ? '📄 Agent' : 
                     msg.type === 'system' ? 'ℹ️ System' :
                     msg.type === 'success' ? '✅ Success' :
                     msg.type === 'info' ? '💡 Info' :
                     msg.type === 'warning' ? '⚠️ Warning' : '⚠️ Error'}
                  </strong>
                  <p className="message-text">{msg.text}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="loading">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span>Processing...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="input-form">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask the CSC Doc Agent..."
              disabled={loading}
              className="text-input"
            />
            <button type="submit" disabled={loading || !input.trim()} className="send-btn">
              {loading ? '⏳' : '📤'} Send
            </button>
          </form>
        </div>
          </>
        )}

        {/* Admin Tab Content */}
        {activeTab === 'admin' && isAdmin && (
          <AdminPanel />
        )}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        userEmail={userEmail}
      />
    </div>
  );
}

export default App;
